import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  HStack,
  Badge,
  IconButton,
  VStack,
  Text,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  InputGroup,
  InputRightElement,
  Spinner,
  Box as ChakraBox,
  useBreakpointValue,
  Checkbox,
  CheckboxGroup,
  Collapse,
  Icon,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw, FiMenu, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { userMemberApi, employeeApi, menuApi, userMenuApi, menuCategoryApi, menuSubCategoryApi } from '../config/api';
import * as FiIcons from 'react-icons/fi';

const toSecureUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://')) return `https://${url.slice(7)}`;
  if (url.startsWith('https://')) return url;
  return `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`;
};

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isEmployeeSelected, setIsEmployeeSelected] = useState(false);
  const toast = useToast();
  
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isMenuAssignOpen, onOpen: onMenuAssignOpen, onClose: onMenuAssignClose } = useDisclosure();
  const cancelRef = React.useRef();
  
  // Menu assignment states
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [userMenus, setUserMenus] = useState([]);
  const [selectedMenus, setSelectedMenus] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubCategories, setExpandedSubCategories] = useState(new Set());
  const [assigningUser, setAssigningUser] = useState(null);
  const [loadingMenus, setLoadingMenus] = useState(false);

  const [formData, setFormData] = useState({
    nip: '',
    name: '',
    position_name: '',
    organization_code: '',
    organization_name: '',
    unit_name: '',
    group_name: '',
    division_name: '',
    status: 1,
    admin_status: '',
    role: [],
    login_type: '',
    password: '',
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Cek apakah integratedToken ada
      const integratedToken = localStorage.getItem('integratedToken');
      if (!integratedToken) {
        toast({
          title: 'Token tidak ditemukan',
          description: 'Silakan login ulang untuk mendapatkan token',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      const response = await userMemberApi.get('/list');
      if (response.data.status === 200) {
        setUsers(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data users',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      
      // Handle 401 error dengan lebih baik
      if (error.response?.status === 401) {
        toast({
          title: 'Unauthorized',
          description: 'Token tidak valid atau telah expired. Silakan login ulang.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // Jangan langsung logout, biarkan user tahu masalahnya
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.message || error.message || 'Terjadi kesalahan saat mengambil data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle role change
  const handleRoleChange = (selectedRoles) => {
    setFormData((prev) => ({
      ...prev,
      role: selectedRoles,
    }));
  };

  // Search employee function
  const searchEmployee = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await employeeApi.get(`/search?q=${encodeURIComponent(query)}`);
      console.log('Search response:', response.data);
      
      // Handle different response formats
      if (response.data.success) {
        // Check if response has data property (array) or is direct object
        let results = [];
        
        if (response.data.data && Array.isArray(response.data.data)) {
          // Response has data array
          results = response.data.data;
        } else if (response.data.data && !Array.isArray(response.data.data)) {
          // Response has single data object
          results = [response.data.data];
        } else if (response.data.nip) {
          // Response is direct object (format dari controller - single employee)
          results = [response.data];
        } else if (Array.isArray(response.data)) {
          // Response is direct array
          results = response.data;
        }
        
        if (results.length > 0) {
          setSearchResults(results);
          setShowSuggestions(true);
        } else {
          setSearchResults([]);
          setShowSuggestions(false);
          toast({
            title: 'Tidak ditemukan',
            description: 'Employee tidak ditemukan',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setSearchResults([]);
        setShowSuggestions(false);
        if (response.data.message) {
          toast({
            title: 'Tidak ditemukan',
            description: response.data.message,
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error searching employee:', error);
      console.error('Error response:', error.response?.data);
      setSearchResults([]);
      setShowSuggestions(false);
      
      // Show error toast
      if (error.response?.status !== 401) {
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Gagal mencari employee',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsSearching(false);
    }
  }, [toast]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsEmployeeSelected(false); // Reset selected flag when user types
    
    if (!value) {
      setSearchResults([]);
      setShowSuggestions(false);
    }
  };

  // Debounce search effect
  useEffect(() => {
    // Skip search if employee already selected
    if (isEmployeeSelected) {
      return;
    }

    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchEmployee(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchEmployee, isEmployeeSelected]);

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    setIsEmployeeSelected(true); // Mark as selected to prevent auto-search
    setFormData({
      nip: employee.nip || '',
      name: employee.name || '',
      position_name: employee.position_name || '',
      organization_code: employee.organization_code || '',
      organization_name: employee.organization_name || '',
      unit_name: employee.unit_name || '',
      group_name: employee.group_name || '',
      division_name: employee.division_name || '',
      status: 1,
      admin_status: '',
      role: [],
    });
    setSearchQuery(`${employee.nip} - ${employee.name}`);
    setSearchResults([]);
    setShowSuggestions(false);
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      nip: '',
      name: '',
      position_name: '',
      organization_code: '',
      organization_name: '',
      unit_name: '',
      group_name: '',
      division_name: '',
      status: 1,
      admin_status: '',
      role: [],
      login_type: '',
      password: '',
    });
    setSearchQuery('');
    setSearchResults([]);
    setShowSuggestions(false);
    setIsEmployeeSelected(false);
    onFormOpen();
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    // Handle role - bisa string (semicolon or comma separated) atau array
    let roleArray = [];
    if (user.role) {
      if (Array.isArray(user.role)) {
        roleArray = user.role;
      } else if (typeof user.role === 'string') {
        // Support both semicolon and comma separator
        if (user.role.includes(';')) {
          roleArray = user.role.split(';').map(r => r.trim()).filter(r => r);
        } else {
          roleArray = user.role.split(',').map(r => r.trim()).filter(r => r);
        }
      }
    }
    
    setFormData({
      nip: user.nip,
      name: user.name || '',
      position_name: user.position_name || '',
      organization_code: user.organization_code || '',
      organization_name: user.organization_name || '',
      unit_name: user.unit_name || '',
      group_name: user.group_name || '',
      division_name: user.division_name || '',
      status: user.status !== undefined ? user.status : 1,
      admin_status: user.admin_status || '',
      role: roleArray,
      login_type: user.login_type || '',
      password: '', // Don't show existing password
    });
    onFormOpen();
  };

  const handleSubmit = async () => {
    if (!formData.nip) {
      toast({
        title: 'Validasi gagal',
        description: 'NIP harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Prepare data with role as string separated by semicolon
      let roleValue = '';
      if (formData.role && Array.isArray(formData.role) && formData.role.length > 0) {
        roleValue = formData.role.join(';');
      } else if (formData.role && typeof formData.role === 'string') {
        roleValue = formData.role;
      }
      
      const submitData = {
        ...formData,
        role: roleValue,
        // Only send password if it's provided (not empty)
        password: formData.password && formData.password.trim() !== '' ? formData.password : undefined,
      };

      if (editingUser) {
        // Update - menggunakan POST /update/:nip
        const response = await userMemberApi.post(`/update/${formData.nip}`, submitData);
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'User berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchUsers();
        }
      } else {
        // Create
        const response = await userMemberApi.post('/', submitData);
        if (response.data.status === 201) {
          toast({
            title: 'Berhasil',
            description: 'User berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchUsers();
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteUser(user);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUser) return;

    try {
      // Delete - menggunakan POST /delete/:nip
      const response = await userMemberApi.post(`/delete/${deleteUser.nip}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'User berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteUser(null);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isMobileView = useBreakpointValue({ base: true, md: false });

  // Helper function to format admin status label
  const formatAdminStatus = (status) => {
    const statusMap = {
      'admin': 'Admin',
      'admin_inj': 'Admin INJ',
      'admin_member': 'Admin Member'
    };
    return statusMap[status] || status;
  };

  // Helper function to format role label
  const formatRole = (role) => {
    const roleMap = {
      'ieem': 'IEEM',
      'bp': 'Bundling Product'
    };
    return roleMap[role] || role;
  };

  // Helper function to format roles for display
  const formatRoles = (roles) => {
    if (!roles || (Array.isArray(roles) && roles.length === 0)) {
      return '-';
    }
    
    // Handle array
    if (Array.isArray(roles)) {
      return roles.map(r => formatRole(r)).join(', ');
    }
    
    // Handle string (comma separated)
    if (typeof roles === 'string') {
      return roles.split(',').map(r => formatRole(r.trim())).join(', ');
    }
    
    return formatRole(roles);
  };

  // DataTable columns definition
  const columns = useMemo(
    () => [
      {
        name: 'NIP',
        selector: (row) => row.nip,
        sortable: true,
        width: '150px',
        omit: isMobileView,
      },
      {
        name: 'Nama',
        selector: (row) => row.name || '-',
        sortable: true,
        cell: (row) => (
          <VStack align="start" spacing={1}>
            <Text fontWeight="600" fontSize={{ base: 'sm', lg: 'md' }}>
              {row.name || '-'}
            </Text>
            {isMobileView && (
              <Text fontSize="xs" color="gray.500">
                NIP: {row.nip}
              </Text>
            )}
          </VStack>
        ),
      },
      {
        name: 'Position',
        selector: (row) => row.position_name || '-',
        sortable: true,
        omit: isMobileView,
      },
      {
        name: 'Organization',
        selector: (row) => row.organization_name || '-',
        sortable: true,
        omit: isMobileView,
      },
      {
        name: 'Status',
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <Badge
            colorScheme={row.status === 1 ? 'green' : 'red'}
            fontSize={{ base: 'xs', lg: 'sm' }}
          >
            {row.status === 1 ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        name: 'Admin Status',
        selector: (row) => row.admin_status || '-',
        sortable: true,
        cell: (row) =>
          row.admin_status ? (
            <Badge colorScheme="purple" fontSize={{ base: 'xs', lg: 'sm' }}>
              {formatAdminStatus(row.admin_status)}
            </Badge>
          ) : (
            '-'
          ),
        omit: isMobileView,
      },
      {
        name: 'Login Type',
        selector: (row) => row.login_type || '-',
        sortable: true,
        cell: (row) => {
          const loginType = row.login_type;
          if (loginType === '0') {
            return <Badge colorScheme="orange" fontSize={{ base: 'xs', lg: 'sm' }}>Lokal</Badge>;
          } else if (loginType === '1') {
            return <Badge colorScheme="blue" fontSize={{ base: 'xs', lg: 'sm' }}>Rinjani</Badge>;
          }
          return '-';
        },
        omit: isMobileView,
      },
      {
        name: 'Role',
        selector: (row) => row.role || '-',
        sortable: true,
        cell: (row) => {
          const roles = row.role;
          if (!roles || (Array.isArray(roles) && roles.length === 0)) {
            return '-';
          }
          
          // Handle array or string (semicolon or comma separated)
          let roleArray = [];
          if (Array.isArray(roles)) {
            roleArray = roles;
          } else if (typeof roles === 'string') {
            // Support both semicolon and comma separator
            if (roles.includes(';')) {
              roleArray = roles.split(';').map(r => r.trim()).filter(r => r);
            } else {
              roleArray = roles.split(',').map(r => r.trim()).filter(r => r);
            }
          }
          
          if (roleArray.length === 0) {
            return '-';
          }
          
          return (
            <HStack spacing={1} flexWrap="wrap">
              {roleArray.map((role, index) => (
                <Badge
                  key={index}
                  colorScheme="blue"
                  fontSize={{ base: 'xs', lg: 'sm' }}
                >
                  {formatRole(role)}
                </Badge>
              ))}
            </HStack>
          );
        },
        omit: isMobileView,
      },
      {
        name: 'Aksi',
        cell: (row) => (
          <HStack spacing={1} justify="center" flexWrap="wrap">
            <IconButton
              icon={<FiEdit />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="blue"
              variant="ghost"
              onClick={() => handleEdit(row)}
              aria-label="Edit"
              title="Edit User"
            />
            <IconButton
              icon={<FiMenu />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="purple"
              variant="ghost"
              onClick={() => handleAssignMenu(row)}
              aria-label="Assign Menu"
              title="Assign Menu"
            />
            <IconButton
              icon={<FiTrash2 />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="red"
              variant="ghost"
              onClick={() => handleDeleteClick(row)}
              aria-label="Delete"
              title="Hapus User"
            />
          </HStack>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '120px',
      },
        ],
    [isMobileView, handleEdit, handleDeleteClick]
  );

  // Fetch menus, categories, and subcategories for menu assignment
  const fetchMenuData = useCallback(async () => {
    try {
      const [menusRes, categoriesRes, subCategoriesRes] = await Promise.all([
        menuApi.get('/list'),
        menuCategoryApi.get('/list'),
        menuSubCategoryApi.get('/list')
      ]);

      if (menusRes.data.status === 200) {
        setMenus(menusRes.data.data || []);
      }
      if (categoriesRes.data.status === 200) {
        setCategories(categoriesRes.data.data || []);
      }
      if (subCategoriesRes.data.status === 200) {
        setSubCategories(subCategoriesRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching menu data:', error);
      toast({
        title: 'Error',
        description: 'Gagal mengambil data menu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Fetch user menus
  const fetchUserMenus = useCallback(async (nip) => {
    if (!nip) return;
    setLoadingMenus(true);
    try {
      // Fetch all user menus (both 'be' and 'fe') without type filter
      const response = await userMenuApi.get(`/list/${nip}`);
      if (response.data.status === 200) {
        const userMenuList = response.data.data || [];
        setUserMenus(userMenuList);
        // Create Set of selected menu keys (MENU_ID_MENUSUBCATEGORY_ID)
        const selected = new Set();
        userMenuList.forEach(um => {
          // Handle both cases: MENU_ID can be null/undefined for subcategory-only menus
          // Check if MENU_ID exists and is a valid number
          if (um.MENU_ID !== null && um.MENU_ID !== undefined && um.MENU_ID !== '') {
            // Menu with MENU_ID
            selected.add(`${um.MENU_ID}_${um.MENUSUBCATEGORY_ID}`);
          } else {
            // Subcategory as menu (no MENU_ID)
            selected.add(`0_${um.MENUSUBCATEGORY_ID}`);
          }
        });
        setSelectedMenus(selected);
      }
    } catch (error) {
      console.error('Error fetching user menus:', error);
      setUserMenus([]);
      setSelectedMenus(new Set());
    } finally {
      setLoadingMenus(false);
    }
  }, []);

  // Note: Auto-check is handled by fetchUserMenus which sets selectedMenus based on database
  // Both Back End and Front End: only check items that are already assigned in the database
  // No auto-check for DEV = 'n' items - only show what's actually assigned

  // Handle assign menu button click
  const handleAssignMenu = async (user) => {
    setAssigningUser(user);
    // Reset selected menus first
    setSelectedMenus(new Set());
    // Fetch menu data first, then fetch user menus
    await fetchMenuData();
    // Wait a bit to ensure menu data is set
    await new Promise(resolve => setTimeout(resolve, 100));
    await fetchUserMenus(user.nip);
    onMenuAssignOpen();
  };

  // Toggle menu selection
  const toggleMenuSelection = (menuId, subCategoryId) => {
    const key = `${menuId}_${subCategoryId}`;
    const newSelected = new Set(selectedMenus);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedMenus(newSelected);
  };

  // Toggle subcategory selection (for subcategory as menu)
  const toggleSubCategorySelection = (subCategoryId) => {
    const key = `0_${subCategoryId}`;
    const newSelected = new Set(selectedMenus);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedMenus(newSelected);
  };

  // Save assigned menus
  const handleSaveAssignedMenus = async () => {
    if (!assigningUser) return;

    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const CREATED_BY = userData.nip || userData.username || null;

      // Prepare menus array
      const menusToAssign = [];
      selectedMenus.forEach(key => {
        const [menuId, subCategoryId] = key.split('_');
        menusToAssign.push({
          MENU_ID: parseInt(menuId) || null,
          MENUSUBCATEGORY_ID: parseInt(subCategoryId)
        });
      });

      // Delete all existing user menus first
      await userMenuApi.post(`/delete-all/${assigningUser.nip}`, {
        DELETED_BY: CREATED_BY
      });

      // Bulk create new user menus
      if (menusToAssign.length > 0) {
        await userMenuApi.post('/bulk', {
          nip: assigningUser.nip,
          menus: menusToAssign,
          CREATED_BY
        });
      }

      toast({
        title: 'Berhasil',
        description: 'Menu berhasil di-assign ke user',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onMenuAssignClose();
      setAssigningUser(null);
      setSelectedMenus(new Set());
    } catch (error) {
      console.error('Error saving assigned menus:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan menu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Organize data into tree structure grouped by type (be/fe)
  const treeData = useMemo(() => {
    const treeByType = {
      be: {}, // Back End
      fe: {}, // Front End
      other: {} // No type specified
    };
    
    // First, add all active categories grouped by type
    categories
      .filter(cat => cat.MENUCATEGORY_STATUS === 1)
      .forEach(category => {
        const type = category.MENUCATEGORY_TYPE || 'other';
        const typeKey = type === 'be' ? 'be' : type === 'fe' ? 'fe' : 'other';
        
        if (!treeByType[typeKey]) {
          treeByType[typeKey] = {};
        }
        
        treeByType[typeKey][category.MENUCATEGORY_ID] = {
          category,
          subCategories: {}
        };
      });
    
    // Then, add all active subcategories under their categories
    subCategories
      .filter(sub => sub.MENUSUBCATEGORY_STATUS === 1)
      .forEach(subCategory => {
        const categoryId = subCategory.MENUCATEGORY_ID;
        
        // Find which type group this category belongs to
        const category = categories.find(cat => cat.MENUCATEGORY_ID === categoryId);
        if (category) {
          const type = category.MENUCATEGORY_TYPE || 'other';
          const typeKey = type === 'be' ? 'be' : type === 'fe' ? 'fe' : 'other';
          
          if (treeByType[typeKey] && treeByType[typeKey][categoryId]) {
            treeByType[typeKey][categoryId].subCategories[subCategory.MENUSUBCATEGORY_ID] = {
              subCategory,
              menus: []
            };
          }
        }
      });
    
    // Finally, add menus to their subcategories
    menus.forEach(menu => {
      const categoryId = menu.MENUCATEGORY_ID;
      const subCategoryId = menu.MENUSUBCATEGORY_ID;
      
      // Find which type group this menu belongs to
      const category = categories.find(cat => cat.MENUCATEGORY_ID === categoryId);
      if (category) {
        const type = category.MENUCATEGORY_TYPE || 'other';
        const typeKey = type === 'be' ? 'be' : type === 'fe' ? 'fe' : 'other';
        
        if (treeByType[typeKey]?.[categoryId]?.subCategories[subCategoryId]) {
          treeByType[typeKey][categoryId].subCategories[subCategoryId].menus.push(menu);
        }
      }
    });
    
    return treeByType;
  }, [menus, categories, subCategories]);

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleSubCategory = (subCategoryId) => {
    const newExpanded = new Set(expandedSubCategories);
    if (newExpanded.has(subCategoryId)) {
      newExpanded.delete(subCategoryId);
    } else {
      newExpanded.add(subCategoryId);
    }
    setExpandedSubCategories(newExpanded);
  };

  // Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f7fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
  };

  // Pagination component options
  const paginationComponentOptions = {
    rowsPerPageText: 'Baris per halaman:',
    rangeSeparatorText: 'dari',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Semua',
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!tableSearchTerm) return users;
    const searchLower = tableSearchTerm.toLowerCase();
    return users.filter((user) => {
      return (
        (user.nip && user.nip.toLowerCase().includes(searchLower)) ||
        (user.name && user.name.toLowerCase().includes(searchLower)) ||
        (user.position_name && user.position_name.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower)) ||
        (user.role && user.role.toLowerCase().includes(searchLower))
      );
    });
  }, [users, tableSearchTerm]);

  return (
    <Box>
      <VStack align="stretch" spacing={4} mb={6}>
        <Heading size={{ base: 'md', lg: 'lg' }} color="gray.700">
          Manage Users
        </Heading>
        <HStack spacing={3} flexWrap="wrap">
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchUsers}
            isLoading={loading}
            variant="outline"
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Refresh
          </Button>
          <Button
            leftIcon={<FiPlus />}
            onClick={handleCreate}
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Tambah User</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Tambah</Text>
          </Button>
        </HStack>
      </VStack>

      <Card bg="white" borderRadius="16px" boxShadow="sm">
        <CardBody p={{ base: 2, lg: 4 }}>
          <DataTable
            columns={columns}
            data={filteredUsers}
            progressPending={loading}
            progressComponent={<Text py={8}>Loading...</Text>}
            noDataComponent={
              <Text py={8} color="gray.500" textAlign="center">
                Tidak ada data user
              </Text>
            }
            subHeader
            subHeaderComponent={
              <Input
                placeholder="Cari user (NIP, nama, posisi, email, role)..."
                value={tableSearchTerm}
                onChange={(e) => setTableSearchTerm(e.target.value)}
                mb={4}
                maxW="400px"
              />
            }
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
            paginationComponentOptions={paginationComponentOptions}
            highlightOnHover
            pointerOnHover
            customStyles={customStyles}
            responsive
            dense
            striped
          />
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingUser ? 'Edit User' : 'Tambah User Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {!editingUser && (
                <FormControl isRequired>
                  <FormLabel>Cari Employee (NIP / Nama)</FormLabel>
                  <Box position="relative">
                    <InputGroup>
                      <Input
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder="Cari berdasarkan NIP atau Nama (min 2 karakter)"
                        onFocus={() => {
                          if (searchResults.length > 0) {
                            setShowSuggestions(true);
                          }
                        }}
                        onBlur={(e) => {
                          // Delay to allow click on suggestion
                          // Check if blur is caused by clicking on suggestion
                          const relatedTarget = e.relatedTarget;
                          if (!relatedTarget || !relatedTarget.closest('[data-suggestion]')) {
                            setTimeout(() => setShowSuggestions(false), 200);
                          }
                        }}
                      />
                      {isSearching && (
                        <InputRightElement>
                          <Spinner size="sm" />
                        </InputRightElement>
                      )}
                    </InputGroup>
                    {showSuggestions && searchResults.length > 0 && (
                      <ChakraBox
                        position="absolute"
                        zIndex={9999}
                        w="100%"
                        mt={1}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                        maxH="300px"
                        overflowY="auto"
                      >
                        {searchResults.map((employee, index) => (
                          <Box
                            key={employee.nip || index}
                            data-suggestion
                            p={3}
                            cursor="pointer"
                            _hover={{ bg: 'brand.50' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEmployeeSelect(employee);
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent input blur
                            }}
                            borderBottom={index < searchResults.length - 1 ? '1px solid' : 'none'}
                            borderColor="gray.100"
                          >
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="600" fontSize="sm" color="gray.700">
                                {employee.name || '-'}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                NIP: {employee.nip || '-'}
                              </Text>
                              {employee.position_name && (
                                <Text fontSize="xs" color="gray.500">
                                  {employee.position_name}
                                </Text>
                              )}
                            </VStack>
                          </Box>
                        ))}
                      </ChakraBox>
                    )}
                    {showSuggestions && searchResults.length === 0 && !isSearching && searchQuery.length >= 2 && (
                      <ChakraBox
                        position="absolute"
                        zIndex={9999}
                        w="100%"
                        mt={1}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        boxShadow="lg"
                        p={3}
                      >
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          Tidak ada hasil ditemukan
                        </Text>
                      </ChakraBox>
                    )}
                  </Box>
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>NIP</FormLabel>
                <Input
                  name="nip"
                  value={formData.nip}
                  onChange={handleInputChange}
                  isDisabled={!!editingUser}
                  placeholder="Masukkan NIP"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Nama</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Masukkan nama"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Position Name</FormLabel>
                <Input
                  name="position_name"
                  value={formData.position_name}
                  onChange={handleInputChange}
                  placeholder="Masukkan position name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Organization Code</FormLabel>
                <Input
                  name="organization_code"
                  value={formData.organization_code}
                  onChange={handleInputChange}
                  placeholder="Masukkan organization code"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Organization Name</FormLabel>
                <Input
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleInputChange}
                  placeholder="Masukkan organization name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Unit Name</FormLabel>
                <Input
                  name="unit_name"
                  value={formData.unit_name}
                  onChange={handleInputChange}
                  placeholder="Masukkan unit name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Group Name</FormLabel>
                <Input
                  name="group_name"
                  value={formData.group_name}
                  onChange={handleInputChange}
                  placeholder="Masukkan group name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Division Name</FormLabel>
                <Input
                  name="division_name"
                  value={formData.division_name}
                  onChange={handleInputChange}
                  placeholder="Masukkan division name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Admin Status</FormLabel>
                <Select
                  name="admin_status"
                  value={formData.admin_status}
                  onChange={handleInputChange}
                  placeholder="Pilih admin status"
                >
                  <option value="">-</option>
                  <option value="admin">Admin</option>
                  <option value="admin_inj">Admin INJ</option>
                  <option value="admin_member">Admin Member</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Role</FormLabel>
                <CheckboxGroup
                  value={formData.role || []}
                  onChange={handleRoleChange}
                  colorScheme="brand"
                >
                  <VStack align="start" spacing={2}>
                    <Checkbox value="ieem">
                      IEEM (Injourney Employee Exclusive Membership)
                    </Checkbox>
                    <Checkbox value="bp">
                      Bundling Product
                    </Checkbox>
                  </VStack>
                </CheckboxGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Login Type</FormLabel>
                <Select
                  name="login_type"
                  value={formData.login_type}
                  onChange={handleInputChange}
                  placeholder="Pilih login type"
                >
                  <option value="">-</option>
                  <option value="1">Rinjani (SSO)</option>
                  <option value="0">Lokal</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  1: Rinjani (SSO), 0: Lokal (password di database)
                </Text>
              </FormControl>

              {(formData.login_type === '0' || editingUser?.login_type === '0') && (
                <FormControl isRequired={formData.login_type === '0' && !editingUser}>
                  <FormLabel>Password {editingUser ? '(Kosongkan jika tidak ingin mengubah)' : ''}</FormLabel>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={editingUser ? "Kosongkan jika tidak ingin mengubah" : "Masukkan password"}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Password akan dienkripsi dengan bcrypt (12 rounds)
                  </Text>
                </FormControl>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Batal
            </Button>
            <Button colorScheme="brand" onClick={handleSubmit}>
              {editingUser ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus User
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus user <strong>{deleteUser?.name || deleteUser?.nip}</strong>?
              <br />
              <Text fontSize="sm" color="gray.500" mt={2}>
                (Status akan diubah menjadi inactive)
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Assign Menu Modal */}
      <Modal 
        isOpen={isMenuAssignOpen} 
        onClose={onMenuAssignClose} 
        size={{ base: 'full', md: 'xl', lg: '2xl' }} 
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH={{ base: '100vh', md: '90vh' }}>
          <ModalHeader fontSize={{ base: 'md', md: 'lg' }}>
            Assign Menu - {assigningUser?.name || assigningUser?.nip}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingMenus ? (
              <Box textAlign="center" py={8}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack align="stretch" spacing={6} maxH={{ base: '70vh', md: '60vh' }} overflowY="auto">
                {Object.keys(treeData.be).length === 0 && Object.keys(treeData.fe).length === 0 && Object.keys(treeData.other).length === 0 ? (
                  <Text color="gray.500" textAlign="center" py={4}>
                    Tidak ada data menu
                  </Text>
                ) : (
                  <>
                    {/* Back End Section */}
                    {Object.keys(treeData.be).length > 0 && (
                      <Box>
                        <Box
                          mb={4}
                          p={3}
                          bg="blue.50"
                          borderLeft="4px"
                          borderColor="blue.500"
                          borderRadius="md"
                        >
                          <HStack spacing={2}>
                            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                              BACK END
                            </Badge>
                            <Text fontSize="sm" color="gray.600" fontWeight="medium">
                              Menu untuk Back End System
                            </Text>
                          </HStack>
                        </Box>
                        <VStack align="stretch" spacing={2}>
                          {Object.entries(treeData.be).map(([categoryId, categoryData]) => {
                            const isCategoryExpanded = expandedCategories.has(parseInt(categoryId));
                            return (
                              <Box key={categoryId} border="1px" borderColor="blue.200" borderRadius="md" p={2}>
                                {/* Category Level */}
                                <HStack
                                  spacing={2}
                                  p={2}
                                  cursor="pointer"
                                  onClick={() => toggleCategory(parseInt(categoryId))}
                                  _hover={{ bg: 'blue.50' }}
                                  borderRadius="md"
                                >
                                  <IconButton
                                    icon={isCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                    size="xs"
                                    variant="ghost"
                                    aria-label="Toggle"
                                  />
                                  <Text fontWeight="bold" fontSize="md" flex={1}>
                                    {categoryData.category.MENUCATEGORY_NAME}
                                  </Text>
                                  <Badge colorScheme="blue" fontSize="xs">
                                    Category
                                  </Badge>
                                </HStack>

                        {/* Sub Categories */}
                        <Collapse in={isCategoryExpanded} animateOpacity>
                          <Box pl={6} mt={2}>
                            {Object.entries(categoryData.subCategories).map(([subCategoryId, subCategoryData]) => {
                              const isSubCategoryExpanded = expandedSubCategories.has(parseInt(subCategoryId));
                              const hasMenus = subCategoryData.menus.length > 0;
                              const shouldShowAsMenu = !hasMenus || subCategoryData.subCategory.MENUSUBCATEGORY_HASMENU === 0;
                              const subCategoryKey = `0_${subCategoryId}`;
                              const isSubCategorySelected = selectedMenus.has(subCategoryKey);

                              // If subcategory is a menu itself
                              if (shouldShowAsMenu) {
                                return (
                                  <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="purple.300" pl={{ base: 2, md: 3 }}>
                                    <HStack spacing={2} p={{ base: 1.5, md: 2 }} flexWrap="wrap">
                                      <Checkbox
                                        isChecked={isSubCategorySelected}
                                        onChange={() => toggleSubCategorySelection(parseInt(subCategoryId))}
                                        colorScheme="purple"
                                        size={{ base: 'sm', md: 'md' }}
                                      />
                                      {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                        <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize={{ base: 'sm', md: 'md' }} color="purple.500" />
                                      )}
                                      <Text fontWeight="medium" fontSize={{ base: 'xs', md: 'sm' }} flex={1} minW="120px">
                                        {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                      </Text>
                                      <Badge colorScheme="purple" fontSize="xs" variant="outline">
                                        Sub Category (Menu)
                                      </Badge>
                                    </HStack>
                                  </Box>
                                );
                              }

                              // If subcategory has menus
                              return (
                                <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="gray.300" pl={{ base: 2, md: 3 }}>
                                  <HStack
                                    spacing={2}
                                    p={{ base: 1.5, md: 2 }}
                                    cursor="pointer"
                                    onClick={() => toggleSubCategory(parseInt(subCategoryId))}
                                    _hover={{ bg: 'gray.50' }}
                                    borderRadius="md"
                                    flexWrap="wrap"
                                  >
                                    <IconButton
                                      icon={isSubCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                      size="xs"
                                      variant="ghost"
                                      aria-label="Toggle"
                                    />
                                    {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                      <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize={{ base: 'sm', md: 'md' }} color="purple.500" />
                                    )}
                                    <Text fontWeight="semibold" fontSize={{ base: 'xs', md: 'sm' }} flex={1} minW="120px">
                                      {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                    </Text>
                                    <Badge colorScheme="purple" fontSize="xs">
                                      Sub Category
                                    </Badge>
                                  </HStack>

                                  {/* Menus */}
                                  <Collapse in={isSubCategoryExpanded} animateOpacity>
                                    <Box pl={6} mt={2}>
                                      {subCategoryData.menus.map((menu) => {
                                        const menuKey = `${menu.MENU_ID}_${subCategoryId}`;
                                        const isMenuSelected = selectedMenus.has(menuKey);
                                        return (
                                          <Box
                                            key={menu.MENU_ID}
                                            p={{ base: 1.5, md: 2 }}
                                            mb={1}
                                            borderLeft="2px"
                                            borderColor="blue.200"
                                            bg="blue.50"
                                            borderRadius="md"
                                          >
                                            <HStack spacing={2} flexWrap="wrap">
                                              <Checkbox
                                                isChecked={isMenuSelected}
                                                onChange={() => toggleMenuSelection(menu.MENU_ID, parseInt(subCategoryId))}
                                                colorScheme="blue"
                                                size={{ base: 'sm', md: 'md' }}
                                              />
                                              <VStack align="start" spacing={0} flex={1} minW="120px">
                                                <HStack spacing={2} flexWrap="wrap">
                                                  <Text fontWeight="medium" fontSize={{ base: 'xs', md: 'sm' }}>
                                                    {menu.MENU_NAME}
                                                  </Text>
                                                  <Badge
                                                    colorScheme={menu.MENU_STATUS === 1 ? 'green' : 'red'}
                                                    fontSize="xs"
                                                  >
                                                    {menu.MENU_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                  </Badge>
                                                </HStack>
                                                {menu.MENU_URL && (
                                                  <Text
                                                    fontSize="xs"
                                                    color="blue.500"
                                                    isTruncated
                                                    maxW={{ base: '200px', md: '300px' }}
                                                    wordBreak="break-all"
                                                  >
                                                    {toSecureUrl(menu.MENU_URL)}
                                                  </Text>
                                                )}
                                              </VStack>
                                            </HStack>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}

                    {/* Front End Section */}
                    {Object.keys(treeData.fe).length > 0 && (
                      <Box>
                        <Box
                          mb={4}
                          p={3}
                          bg="purple.50"
                          borderLeft="4px"
                          borderColor="purple.500"
                          borderRadius="md"
                        >
                          <HStack spacing={2}>
                            <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                              FRONT END
                            </Badge>
                            <Text fontSize="sm" color="gray.600" fontWeight="medium">
                              Menu untuk Front End System
                            </Text>
                          </HStack>
                        </Box>
                        <VStack align="stretch" spacing={2}>
                          {Object.entries(treeData.fe).map(([categoryId, categoryData]) => {
                            const isCategoryExpanded = expandedCategories.has(parseInt(categoryId));
                            return (
                              <Box key={categoryId} border="1px" borderColor="purple.200" borderRadius="md" p={2}>
                                {/* Category Level */}
                                <HStack
                                  spacing={2}
                                  p={2}
                                  cursor="pointer"
                                  onClick={() => toggleCategory(parseInt(categoryId))}
                                  _hover={{ bg: 'purple.50' }}
                                  borderRadius="md"
                                >
                                  <IconButton
                                    icon={isCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                    size="xs"
                                    variant="ghost"
                                    aria-label="Toggle"
                                  />
                                  <Text fontWeight="bold" fontSize="md" flex={1}>
                                    {categoryData.category.MENUCATEGORY_NAME}
                                  </Text>
                                  <Badge colorScheme="purple" fontSize="xs">
                                    Category
                                  </Badge>
                                </HStack>

                                {/* Sub Categories */}
                                <Collapse in={isCategoryExpanded} animateOpacity>
                                  <Box pl={6} mt={2}>
                                    {Object.entries(categoryData.subCategories).map(([subCategoryId, subCategoryData]) => {
                                      const isSubCategoryExpanded = expandedSubCategories.has(parseInt(subCategoryId));
                                      const hasMenus = subCategoryData.menus.length > 0;
                                      const shouldShowAsMenu = !hasMenus || subCategoryData.subCategory.MENUSUBCATEGORY_HASMENU === 0;
                                      const subCategoryKey = `0_${subCategoryId}`;
                                      const isSubCategorySelected = selectedMenus.has(subCategoryKey);

                                      // If subcategory is a menu itself
                                      if (shouldShowAsMenu) {
                                        return (
                                          <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="purple.300" pl={{ base: 2, md: 3 }}>
                                            <HStack spacing={2} p={{ base: 1.5, md: 2 }} flexWrap="wrap">
                                              <Checkbox
                                                isChecked={isSubCategorySelected}
                                                onChange={() => toggleSubCategorySelection(parseInt(subCategoryId))}
                                                colorScheme="purple"
                                                size={{ base: 'sm', md: 'md' }}
                                              />
                                              {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                                <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize={{ base: 'sm', md: 'md' }} color="purple.500" />
                                              )}
                                              <Text fontWeight="medium" fontSize={{ base: 'xs', md: 'sm' }} flex={1} minW="120px">
                                                {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                              </Text>
                                              <Badge colorScheme="purple" fontSize="xs" variant="outline">
                                                Sub Category (Menu)
                                              </Badge>
                                            </HStack>
                                          </Box>
                                        );
                                      }

                                      // If subcategory has menus
                                      return (
                                        <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="gray.300" pl={{ base: 2, md: 3 }}>
                                          <HStack
                                            spacing={2}
                                            p={{ base: 1.5, md: 2 }}
                                            cursor="pointer"
                                            onClick={() => toggleSubCategory(parseInt(subCategoryId))}
                                            _hover={{ bg: 'gray.50' }}
                                            borderRadius="md"
                                            flexWrap="wrap"
                                          >
                                            <IconButton
                                              icon={isSubCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                              size="xs"
                                              variant="ghost"
                                              aria-label="Toggle"
                                            />
                                            {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                              <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize={{ base: 'sm', md: 'md' }} color="purple.500" />
                                            )}
                                            <Text fontWeight="semibold" fontSize={{ base: 'xs', md: 'sm' }} flex={1} minW="120px">
                                              {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                            </Text>
                                            <Badge colorScheme="purple" fontSize="xs">
                                              Sub Category
                                            </Badge>
                                          </HStack>

                                          {/* Menus */}
                                          <Collapse in={isSubCategoryExpanded} animateOpacity>
                                            <Box pl={6} mt={2}>
                                              {subCategoryData.menus.map((menu) => {
                                                const menuKey = `${menu.MENU_ID}_${subCategoryId}`;
                                                const isMenuSelected = selectedMenus.has(menuKey);
                                                return (
                                                  <Box
                                                    key={menu.MENU_ID}
                                                    p={{ base: 1.5, md: 2 }}
                                                    mb={1}
                                                    borderLeft="2px"
                                                    borderColor="blue.200"
                                                    bg="blue.50"
                                                    borderRadius="md"
                                                  >
                                                    <HStack spacing={2} flexWrap="wrap">
                                                      <Checkbox
                                                        isChecked={isMenuSelected}
                                                        onChange={() => toggleMenuSelection(menu.MENU_ID, parseInt(subCategoryId))}
                                                        colorScheme="blue"
                                                        size={{ base: 'sm', md: 'md' }}
                                                      />
                                                      <VStack align="start" spacing={0} flex={1} minW="120px">
                                                        <HStack spacing={2} flexWrap="wrap">
                                                          <Text fontWeight="medium" fontSize={{ base: 'xs', md: 'sm' }}>
                                                            {menu.MENU_NAME}
                                                          </Text>
                                                          <Badge
                                                            colorScheme={menu.MENU_STATUS === 1 ? 'green' : 'red'}
                                                            fontSize="xs"
                                                          >
                                                            {menu.MENU_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                          </Badge>
                                                        </HStack>
                                                        {menu.MENU_URL && (
                                                          <Text
                                                            fontSize="xs"
                                                            color="blue.500"
                                                            isTruncated
                                                            maxW={{ base: '200px', md: '300px' }}
                                                            wordBreak="break-all"
                                                          >
                                                            {toSecureUrl(menu.MENU_URL)}
                                                          </Text>
                                                        )}
                                                      </VStack>
                                                    </HStack>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          </Collapse>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Collapse>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}

                    {/* Other Section (No Type) */}
                    {Object.keys(treeData.other).length > 0 && (
                      <Box>
                        <Box
                          mb={4}
                          p={3}
                          bg="gray.50"
                          borderLeft="4px"
                          borderColor="gray.400"
                          borderRadius="md"
                        >
                          <HStack spacing={2}>
                            <Badge colorScheme="gray" fontSize="md" px={3} py={1}>
                              OTHER
                            </Badge>
                            <Text fontSize="sm" color="gray.600" fontWeight="medium">
                              Menu tanpa type (belum dikategorikan)
                            </Text>
                          </HStack>
                        </Box>
                        <VStack align="stretch" spacing={2}>
                          {Object.entries(treeData.other).map(([categoryId, categoryData]) => {
                            const isCategoryExpanded = expandedCategories.has(parseInt(categoryId));
                            return (
                              <Box key={categoryId} border="1px" borderColor="gray.200" borderRadius="md" p={2}>
                                {/* Category Level */}
                                <HStack
                                  spacing={2}
                                  p={2}
                                  cursor="pointer"
                                  onClick={() => toggleCategory(parseInt(categoryId))}
                                  _hover={{ bg: 'gray.50' }}
                                  borderRadius="md"
                                >
                                  <IconButton
                                    icon={isCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                    size="xs"
                                    variant="ghost"
                                    aria-label="Toggle"
                                  />
                                  <Text fontWeight="bold" fontSize="md" flex={1}>
                                    {categoryData.category.MENUCATEGORY_NAME}
                                  </Text>
                                  <Badge colorScheme="gray" fontSize="xs">
                                    Category
                                  </Badge>
                                </HStack>

                                {/* Sub Categories */}
                                <Collapse in={isCategoryExpanded} animateOpacity>
                                  <Box pl={6} mt={2}>
                                    {Object.entries(categoryData.subCategories).map(([subCategoryId, subCategoryData]) => {
                                      const isSubCategoryExpanded = expandedSubCategories.has(parseInt(subCategoryId));
                                      const hasMenus = subCategoryData.menus.length > 0;
                                      const shouldShowAsMenu = !hasMenus || subCategoryData.subCategory.MENUSUBCATEGORY_HASMENU === 0;
                                      const subCategoryKey = `0_${subCategoryId}`;
                                      const isSubCategorySelected = selectedMenus.has(subCategoryKey);

                                      // If subcategory is a menu itself
                                      if (shouldShowAsMenu) {
                                        return (
                                          <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="purple.300" pl={{ base: 2, md: 3 }}>
                                            <HStack spacing={2} p={{ base: 1.5, md: 2 }} flexWrap="wrap">
                                              <Checkbox
                                                isChecked={isSubCategorySelected}
                                                onChange={() => toggleSubCategorySelection(parseInt(subCategoryId))}
                                                colorScheme="purple"
                                                size={{ base: 'sm', md: 'md' }}
                                              />
                                              {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                                <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize={{ base: 'sm', md: 'md' }} color="purple.500" />
                                              )}
                                              <Text fontWeight="medium" fontSize={{ base: 'xs', md: 'sm' }} flex={1} minW="120px">
                                                {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                              </Text>
                                              <Badge colorScheme="purple" fontSize="xs" variant="outline">
                                                Sub Category (Menu)
                                              </Badge>
                                            </HStack>
                                          </Box>
                                        );
                                      }

                                      // If subcategory has menus
                                      return (
                                        <Box key={subCategoryId} mb={2} borderLeft="2px" borderColor="gray.300" pl={{ base: 2, md: 3 }}>
                                          <HStack
                                            spacing={2}
                                            p={{ base: 1.5, md: 2 }}
                                            cursor="pointer"
                                            onClick={() => toggleSubCategory(parseInt(subCategoryId))}
                                            _hover={{ bg: 'gray.50' }}
                                            borderRadius="md"
                                            flexWrap="wrap"
                                          >
                                            <IconButton
                                              icon={isSubCategoryExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                              size="xs"
                                              variant="ghost"
                                              aria-label="Toggle"
                                            />
                                            {subCategoryData.subCategory.MENUSUBCATEGORY_ICON && FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON] && (
                                              <Icon as={FiIcons[subCategoryData.subCategory.MENUSUBCATEGORY_ICON]} fontSize={{ base: 'sm', md: 'md' }} color="purple.500" />
                                            )}
                                            <Text fontWeight="semibold" fontSize={{ base: 'xs', md: 'sm' }} flex={1} minW="120px">
                                              {subCategoryData.subCategory.MENUSUBCATEGORY_NAME}
                                            </Text>
                                            <Badge colorScheme="purple" fontSize="xs">
                                              Sub Category
                                            </Badge>
                                          </HStack>

                                          {/* Menus */}
                                          <Collapse in={isSubCategoryExpanded} animateOpacity>
                                            <Box pl={6} mt={2}>
                                              {subCategoryData.menus.map((menu) => {
                                                const menuKey = `${menu.MENU_ID}_${subCategoryId}`;
                                                const isMenuSelected = selectedMenus.has(menuKey);
                                                return (
                                                  <Box
                                                    key={menu.MENU_ID}
                                                    p={{ base: 1.5, md: 2 }}
                                                    mb={1}
                                                    borderLeft="2px"
                                                    borderColor="blue.200"
                                                    bg="blue.50"
                                                    borderRadius="md"
                                                  >
                                                    <HStack spacing={2} flexWrap="wrap">
                                                      <Checkbox
                                                        isChecked={isMenuSelected}
                                                        onChange={() => toggleMenuSelection(menu.MENU_ID, parseInt(subCategoryId))}
                                                        colorScheme="blue"
                                                        size={{ base: 'sm', md: 'md' }}
                                                      />
                                                      <VStack align="start" spacing={0} flex={1} minW="120px">
                                                        <HStack spacing={2} flexWrap="wrap">
                                                          <Text fontWeight="medium" fontSize={{ base: 'xs', md: 'sm' }}>
                                                            {menu.MENU_NAME}
                                                          </Text>
                                                          <Badge
                                                            colorScheme={menu.MENU_STATUS === 1 ? 'green' : 'red'}
                                                            fontSize="xs"
                                                          >
                                                            {menu.MENU_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
                                                          </Badge>
                                                        </HStack>
                                                        {menu.MENU_URL && (
                                                          <Text
                                                            fontSize="xs"
                                                            color="blue.500"
                                                            isTruncated
                                                            maxW={{ base: '200px', md: '300px' }}
                                                            wordBreak="break-all"
                                                          >
                                                            {toSecureUrl(menu.MENU_URL)}
                                                          </Text>
                                                        )}
                                                      </VStack>
                                                    </HStack>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          </Collapse>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Collapse>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onMenuAssignClose}
              size={{ base: 'sm', md: 'md' }}
              w={{ base: '50%', md: 'auto' }}
            >
              Batal
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSaveAssignedMenus}
              size={{ base: 'sm', md: 'md' }}
              w={{ base: '50%', md: 'auto' }}
            >
              Simpan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ManageUsers;

